import $ from "jquery";
import PropTypes from "prop-types";
import React from "react";

/**
 * Create a modal dialog by wrapping React components and/or HTML.
 *
 * @example
 *   import Modal, { ModalBody, ModalFooter, ModalHeader } from "components/modal";
 *   <Modal isOpen={this.state.isOpen}>
 *     <ModalHeader title="Hello world" />
 *     <ModalBody>
 *       Modal content here.
 *     </ModalBody>
 *     <ModalFooter>
 *       <button className="btn btn-primary">Submit</button>
 *     </ModalFooter>
 *   </Modal>
 */
class Modal extends React.Component {
  /**
   * Creates the component with the initial state.
   *
   * @param {object} props Initial props
   */
  constructor(props) {
    super(props);

    this.state = { isOpen: false };
    // Bind functions to make `this` available
    this.close = this.close.bind(this);
    this.open = this.open.bind(this);
  }

  /**
   * Closes the modal.
   */
  close() {
    $(this.refs.modal).modal("hide");
  }

  /**
   * Opens the modal when the component is mounted if the `isOpen` prop is set to `true`.
   */
  componentDidMount() {
    if (this.props.isOpen) {
      this.open();
    }
  }

  /**
   * Handles opening and closing the modal when the `isOpen` prop changes.
   *
   * @param {object} nextProps the new component props
   */
  componentWillReceiveProps(nextProps) {
    if (nextProps.isOpen && !this.state.isOpen) {
      this.open();
    } else if (!nextProps.isOpen && this.state.isOpen) {
      this.close();
    }
  }

  /**
   * Returns the context for child components. This is used to pass the modal instance
   * to the child components so the modal can be closed from a child component.
   *
   * @returns {object} The context for child components
   */
  getChildContext() {
    return { modal: this };
  }

  /**
   * Opens the modal.
   */
  open() {
    // The state is first set to `isOpen: true` so the modal element is rendered to the DOM
    // and is available via `this.ref.modal` before performing the Bootstrap modal function.
    this.setState({ isOpen: true }, () => {
      const $modal = $(this.refs.modal);
      $modal.modal("show");

      $modal.on("hidden.bs.modal", () => {
        $modal.off("hidden.bs.modal");
        this.setState({ isOpen: false });
        if (this.props.onClose != null) {
          this.props.onClose();
        }
      });
    });
  }

  /**
   * Renders the modal dialog. Nothing is rendered when the modal isn't open.
   *
   * @returns {object} The React element
   */
  render() {
    if (!this.state.isOpen) {
      return null;
    }

    return (
      <div className={`modal fade ${this.props.className}`} ref="modal">
        <div className="modal-dialog">
          <div className="modal-content">{this.props.children}</div>
        </div>
      </div>
    );
  }
}

Modal.propTypes = {
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
};

Modal.childContextTypes = {
  modal: PropTypes.object
};

export { default as ModalBody } from "./modal-body";
export { default as ModalFooter } from "./modal-footer";
export { default as ModalHeader } from "./modal-header";

export default Modal;
